<script setup>
import {provide, reactive, ref} from "vue";
import useForm from "@/composables/useForm";
import startupsService from "@/services/startups.service";
import PrimaryButton from "../../../../../frontend/src/components/ui/PrimaryButton.vue";
import StartupEditGeneral from "@/components/startups/startupEdit/StartupEditGeneral.vue";
import StartupEditTeam from "@/components/startups/startupEdit/StartupEditTeam.vue";
import StartupEditFunding from "@/components/startups/startupEdit/StartupEditFunding.vue";
import StartupEditContacts from "@/components/startups/startupEdit/StartupEditContacts.vue";
import StartupEditLocation from "@/components/startups/startupEdit/StartupEditLocation.vue";
import StartupEditSocials from "@/components/startups/startupEdit/StartupEditSocials.vue";
import StartupEditFeatures from "@/components/startups/startupEdit/StartupEditFeatures.vue";
import StartupEditPatents from "@/components/startups/startupEdit/StartupEditPatents.vue";
import StartupEditAIFields from "@/components/startups/startupEdit/StartupEditAIFields.vue";

const showModal = ref(false);
const id = ref(null);
const dataLoaded = ref(false);
const emit = defineEmits(['saved']);

const fields = {
    name: "",
    logo: "",
    shortDescription: "",
    description: "",
    founded: "",
    categories: [],
    website: "",
    numEmployeesMin: "",
    numEmployeesMax: "",
    revenueRange: "",
    stage: "",
    totalRaised: "",
    companyPhone: "",
    address1: "",
    address2: "",
    countryId: "",
    stateId: "",
    regionName: "",
    city: "",
    zip: "",
    facebookUrl: "",
    twitterUrl: "",
    linkedinUrl: "",
    websiteKeywords: "",
    hidden: 0,
    isPublic: 0,
    spInactive: 0,
    customCategories: "",
    clients: "",
    features: "",
    founder: "",
    patents: [],
    geminiTrlScore: "",
    geminiTam: "",
    geminiFoundersHistory: "",
    geminiPatents: "",
    geminiGeographicalPresence: "",
    logoUpdated: false,
};

const form = reactive({...fields});
const lastUpdates = reactive(Object.keys(form).reduce((acc, key) => {
    acc[key] = "";
    return acc;
}, {}));

const {populateForm, resetForm, errors, clearErrors, hasErrors} = useForm(form);
const {populateForm: populateLastUpdates} = useForm(lastUpdates);
const tabs = ['General', 'Location', 'Contacts', 'Team', 'Funding', 'Socials', 'Features', 'Patents', 'AI Fields'];
const currentTab = ref('General');
const loading = ref(false);
const hasAiFieldsLogs = ref(false);

async function getData() {
    const {data} = await startupsService.getEdit(id.value);
    populateForm(data.data);
    populateLastUpdates(data.last_update);
    dataLoaded.value = true;
    hasAiFieldsLogs.value = data.has_ai_fields_logs;
}

async function save() {
    loading.value = true

    try {
        const {data} = await startupsService.save(id.value, form);
        if (data.errors.url) {
            data.errors.website = data.errors.url;
        }

        if (!hasErrors(data)) {
            emit('saved');
            showModal.value = false;
        }
    } finally {
        loading.value = false
    }
}

function open(itemId) {
    dataLoaded.value = false;
    resetForm();
    id.value = itemId;
    clearErrors();
    showModal.value = true;
    getData();
}

defineExpose({
    open
});

provide('id', id);
provide('form', form);
provide('lastUpdates', lastUpdates);
provide('errors', errors);
</script>

<template>
    <b-modal v-model="showModal" id="modal-startup-form" modal-class="modal-w-xxl" title="Startup Profile">
        <form class="form pb-5">
            <ul class="nav nav-tabs">
                <li v-for="tab in tabs" :key="tab" class="nav-item">
                    <a class="nav-link" :class="{'active': currentTab === tab}" @click="currentTab = tab">{{tab}}</a>
                </li>
            </ul>

            <div v-if="dataLoaded" class="mt-3">
                <StartupEditGeneral v-if="currentTab === 'General'"/>
                <StartupEditLocation v-if="currentTab === 'Location'"/>
                <StartupEditContacts v-if="currentTab === 'Contacts'"/>
                <StartupEditTeam v-if="currentTab === 'Team'"/>
                <StartupEditFunding v-if="currentTab === 'Funding'"/>
                <StartupEditSocials v-if="currentTab === 'Socials'"/>
                <StartupEditFeatures v-if="currentTab === 'Features'"/>
                <StartupEditPatents v-if="currentTab === 'Patents'"/>
                <StartupEditAIFields
                    v-if="currentTab === 'AI Fields'"
                    :hasAiFieldsLogs="hasAiFieldsLogs"
                />
            </div>
        </form>

        <template #modal-footer>
            <PrimaryButton
                :loading="loading"
                @click="save"
            />
        </template>
    </b-modal>
</template>

<style scoped>

</style>
